import { useMemo, useState, useEffect } from "react";
import { Button, DatePicker, Select } from "antd";
import useAxiosInstance from "../../../lib/useAxiosInstance";
import GenderPieChart from "./GmsStats/GenderPieChart";
import TopCategoriesBar from "./GmsStats/TopCategoriesBar";
import OfficeWiseChart from "./GmsStats/OfficeWiseChart";
import { LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid, } from "recharts";
import { useTranslation } from "react-i18next";

const { RangePicker } = DatePicker;
const { Option } = Select;

const GrievanceStatsPanel = ({ grievances, selectedCategory, setSelectedCategory }) => {
    const { t } = useTranslation();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [duration, setDuration] = useState("Past Week");
  const [data, setData] = useState(null);
  const [durationData, setDurationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTrends, setShowTrends] = useState(false);

  const axiosInstance = useAxiosInstance();

  // Handle range picker
  useEffect(() => {
    if (dateRange?.length === 2) {
      const [start, end] = dateRange;

      setStartDate(start.startOf("day").toISOString());
      setEndDate(end.endOf("day").toISOString());
    } else {
      setStartDate("");
      setEndDate("");
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
    fetchDurationData();
  }, [startDate, endDate, duration]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `api/grievances/getGrievanceInsights?startDate=${startDate}&endDate=${endDate}`
      );
      setData(response?.data?.data);
    } catch (err) {
      console.error("Fetch Insights Error", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDurationData = async () => {
    try {
      const response = await axiosInstance.get(
        `/api/grievances/getGrievanceDurationCount?startDate=${startDate}&endDate=${endDate}&duration=${duration}`
      );
      setDurationData(response?.data?.data || []);
    } catch (err) {
      console.error("Fetch Duration Error", err);
    }
  };


  // Extract unique categories
  const categories = useMemo(() => {
    const unique = new Set();
    grievances.forEach((g) => {
      if (g?.properties?.cat_name) unique.add(g.properties.cat_name);
    });
    return Array.from(unique);
  }, [grievances]);

  // Add this derived value
const selectedCategoryCount = useMemo(() => {
  if (!selectedCategory) return 0;
  return grievances.filter(
    (g) => g?.properties?.cat_name === selectedCategory
  ).length;
}, [selectedCategory, grievances]);

  // Subcategories for selected category
  const subCategories = useMemo(() => {
    if (!selectedCategory) return [];
    const filtered = grievances.filter(
      (g) => g?.properties?.cat_name === selectedCategory
    );
    const subMap = {};
    filtered.forEach((g) => {
      const sub = g?.properties?.sub_cat_name || "Uncategorized";
      subMap[sub] = (subMap[sub] || 0) + 1;
    });
    return Object.entries(subMap).map(([subCat, count]) => ({
      subCat,
      count,
    }));
  }, [selectedCategory, grievances]);

  if (loading) {
    return (
      <div className="absolute top-16 right-4 bg-white/70 rounded-2xl shadow-lg w-[420px] h-[80vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!data) return null;
  return (
    <div className="absolute top-[2%] right-4 bg-white/50 backdrop-blur-xl border border-white/40 rounded-3xl shadow-lg w-[420px] max-h-[90vh] overflow-y-auto z-[9] flex flex-col p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-10">
        <h3 className="text-lg font-bold text-[#004877]">{t("subCategories")}</h3>
        {/* <button
          size="small"
          type="primary"
          className="shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                relative overflow-hidden rounded-xl border backdrop-blur-xl
                border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 pl-2 pr-2 text-[#004877] text-sm"
          onClick={() => setShowTrends(!showTrends)}
        >
          {showTrends ? "Show Insights" : "Show Trends"}
        </button> */}
      </div>

      {/* Filters */}
      <div className="flex gap-3 justify-center">
        {/* <RangePicker
          allowClear
          value={dateRange}
          onChange={(dates) => setDateRange(dates)}
          placeholder={["Start Date", "End Date"]}
          size="middle"
          className="shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                relative overflow-hidden rounded-xl border backdrop-blur-xl
                border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 p-2"
        /> */}
        <RangePicker
          allowClear
          value={dateRange}
          getPopupContainer={(trigger) => trigger.parentElement}
          onChange={(dates) => {
            if (!dates) {
              setDateRange([]);
              return;
            }

            setDateRange(dates);
          }}
          format="DD-MM-YYYY"
          placeholder={["Start Date", "End Date"]}
          size="middle"
          className="shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
            relative overflow-hidden rounded-xl border backdrop-blur-xl
            border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 p-2"
        />
        {!selectedCategory && <button
          size="small"
          type="primary"
          className="shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                relative overflow-hidden rounded-xl border backdrop-blur-xl
                border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 pl-2 pr-2 text-[#004877] text-sm"
          onClick={() => setShowTrends(!showTrends)}
        >
          {showTrends ? t("showInsights") : t("showTrends")}
        </button>}
      </div>
      {/* Category Dropdown */}
      <div className="flex justify-between items-center flex-col">
         {selectedCategory && (
      <p className="text-sm txt-center font-medium text-[#004877]">
    <span className="flex items-center justify-center w-12 h-12 
                    text-2xl font-extrabold text-[#004877] 
                    border-2 border-black rounded-full">
      {selectedCategoryCount}
    </span>
  </p>
    )}
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            {t("filterByCategory")}
          </label>
          <select
            className="w-full mb-3 text-black shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                      relative overflow-hidden rounded-xl border backdrop-blur-xl
                      border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 p-2 px-2 py-1 outline-none"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option className="text-black/60" value="">
              {t("allCategories")}
            </option>
            {categories.map((cat) => (
              <option key={cat} className="text-black" value={cat}>
                {cat}
              </option>
            ))}
          </select>
      </div>

      {!selectedCategory && showTrends && durationData && durationData?.durationCount?.length > 0 && (
        <div>
          
          <div className="shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                relative overflow-hidden rounded-xl border backdrop-blur-xl
                border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 pl-2 pr-2">
            <div className="flex justify-center items-center gap-4">
              {/* <h4 className="text-md font-bold text-[#004877] mb-2">Grievance Trends</h4> */}
              <div className="shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                relative overflow-hidden rounded-xl border backdrop-blur-xl
                border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 pl-2 pr-2">
                <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                size=""
                className="min-w-[50px] bg-transparent text-[#004877] text-xs"
                >
                  <option value="Past Week">Past Week</option>
                  <option value="Past Month">Past Month</option>
                  <option value="Past Year">Past Year</option>
                </select>
              </div>
            </div>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={durationData?.durationCount}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="date" fontSize={10} />
                  <YAxis fontSize={10} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#004877" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* If category selected → show subcategories */}
      {selectedCategory ? (
        <div className="p-3 bg-white/60 rounded-xl border">
          <h4 className="text-md font-bold text-[#004877] mb-2">
            {t("subCategories")} {selectedCategory}
          </h4>
          <ul className="space-y-1">
            {subCategories.map(({ subCat, count }) => (
              <li
                key={subCat}
                className="flex justify-between items-center text-sm text-gray-700"
              >
                <span>{subCat}</span>
                <span className="font-bold text-[#004877] border rounded-full bg-gray-200 px-2 py-1">
                  {count}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <>
          {/* Charts (only when no category filter applied) */}
           {!showTrends && <div className="flex justify-center gap-3 shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                relative overflow-hidden rounded-2xl border backdrop-blur-xl
                border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50">
              <div className="p-2">
                <h4 className="text-sm font-bold text-[#004877] mb-2">
                  {t("genderDistribution")}
                </h4>
                <div className="h-[7rem]">
                  <GenderPieChart data={data.genderWise || []} />
                </div>
              </div>
              <div className="p-2">
              <h4 className="text-sm font-bold text-[#004877] mb-2">
                {t("officeWiseDistribution")}
              </h4>
              <div className="h-[7rem]">
                <OfficeWiseChart data={data.officeWise || []} />
              </div>
            </div>
          </div>}
          {!showTrends && <div className="shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                relative overflow-hidden rounded-2xl border backdrop-blur-xl
                border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 p-2">
                <div className="p-2">
                  <h4 className="text-md font-bold text-[#004877] mb-2">
                    {t("top5Categories")}
                  </h4>
                  <div className="h-[11rem]">
                    <TopCategoriesBar data={data.categoryWise || []} />
                  </div>
                </div>
          </div>}
        </>
      )}
    </div>
  );
};

export default GrievanceStatsPanel;
